/**
 * Reportes → Facturas pendientes de pago (`/reportes/facturas-pendientes`).
 *
 * Puerto de `Reportes::datos_facturas_pagos_pendientes` +
 * `Credito_model::get_transactions` + `views/menu/reportes/facturas_pagos_pendientes.php`
 * y su `src/reportes/pendingPaymentsInvoices.js`.
 *
 * La pantalla del panel anterior pedía **fecha de inicio, fecha de fin y cliente**
 * (se buscaba por nombre o RUC/DNI con un desplegable AJAX) y pintaba, por cada
 * cliente, sus facturas con total, retención, abonado y **pendiente de pago**, más
 * una tarjeta con los totales del filtro; el export a Excel lo armaba el navegador.
 *
 * Reglas conservadas:
 * - Sólo entran los comprobantes **administrados** que no son de contado
 *   (`admin_venta.condicion_pago <> 'CONTADO'`), que no están anulados
 *   (`estado_sunat` distinto de `Anulado` y `Nota de Credito`) y que **no** son de
 *   pago adelantado (`paidInAdvance <> 1`); el rango va por `fecha_emision`.
 * - Las columnas y los importes son los del original: `total`,
 *   `withholding_amount` (retención), `paidout` (abonado) y `pending_amount`
 *   (pendiente), y el pie suma los cuatro.
 * - La consulta mantiene el `LEFT JOIN` a `credito_venta` por `external_cpe` y el
 *   `GROUP BY` de todas las columnas, que era el apaño del original para no repetir
 *   las facturas de un crédito con varias líneas.
 *
 * Diferencias anotadas:
 * - El buscador de cliente es un `select` con los clientes **que tienen facturas
 *   por cobrar** en el rango (antes era una búsqueda AJAX por nombre o documento);
 *   el filtro sigue siendo `cliente.numero_doc`.
 * - Se añaden dos casillas que el original sólo tenía en el modelo y en ningún
 *   filtro visible: *sólo con saldo* (`ROUND(paidout,2) < ROUND(pending_amount,2)`)
 *   y *sólo vencidas* (`fecha_vencimiento < hoy`), y se marca cada factura vencida
 *   con sus días de atraso.
 * - El CSV y el PDF los sirve la API (antes el Excel se armaba en el navegador).
 */

import { ArrowLeft, Download, FileText, Info, RefreshCw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  useCatalogosFacturasPendientes,
  useFacturasPendientes,
  type FiltrosFacturasPendientes,
} from '../reportes.api';

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Fecha local `AAAA-MM-DD` (sin desfasar por la zona horaria). */
function fechaLocal(fecha: Date): string {
  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(
    fecha.getDate(),
  ).padStart(2, '0')}`;
}

/** Rango por defecto del reporte: el año en curso (la cartera se mira por meses). */
function rangoPorDefecto(): FiltrosFacturasPendientes {
  const hoy = new Date();

  return {
    desde: `${hoy.getFullYear()}-01-01`,
    hasta: fechaLocal(hoy),
    numero_doc: '',
    solo_pendientes: false,
    vencidas: false,
  };
}

/** «agosto 2026» a partir de `2026-08`. */
function etiquetaMes(periodo: string): string {
  const [anio, mes] = periodo.split('-');
  const nombre = MESES[Number(mes) - 1];

  return nombre ? `${nombre} ${anio}` : periodo;
}

export function ReporteFacturasPendientesPage() {
  useDocumentTitle('Reportes · Facturas pendientes de pago');

  const [filtros, setFiltros] = useState<FiltrosFacturasPendientes>(rangoPorDefecto);
  const [aviso, setAviso] = useState<string | null>(null);

  const catalogos = useCatalogosFacturasPendientes({ desde: filtros.desde, hasta: filtros.hasta });
  const consulta = useFacturasPendientes(filtros);
  const datos = consulta.data;
  const resumen = datos?.resumen;

  const filas = useMemo(() => datos?.filas ?? [], [datos]);
  // La tabla se pagina en el navegador; el pie y las tarjetas siguen usando el listado
  // completo.
  const paginacion = usePaginacion(filas, { clave: filtros });
  const porCliente = useMemo(() => datos?.por_cliente ?? [], [datos]);
  const clientes = catalogos.data?.clientes ?? [];

  const cambiar = <C extends keyof FiltrosFacturasPendientes>(
    campo: C,
    valor: FiltrosFacturasPendientes[C],
  ) => setFiltros((previos) => ({ ...previos, [campo]: valor }));

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarFacturasPendientes(filtros), {
        nombre: `facturas-pendientes-${filtros.desde}_${filtros.hasta}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el reporte.');
    }
  };

  const exportarPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfFacturasPendientes(filtros));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    }
  };

  const rangoDe = (mes: string) => {
    const anio = Number(mes.slice(0, 4));
    const numero = Number(mes.slice(5, 7));

    return { desde: `${mes}-01`, hasta: fechaLocal(new Date(anio, numero, 0)) };
  };

  const meses = useMemo(() => {
    const vistos = new Map<string, number>();

    for (const fila of filas) {
      const periodo = (fila.fecha_emision ?? '').slice(0, 7);
      if (periodo) vistos.set(periodo, (vistos.get(periodo) ?? 0) + 1);
    }

    return [...vistos.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([periodo, facturas]) => ({ periodo, facturas }));
  }, [filas]);

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">Facturas pendientes de pago</h1>
          <p className="mt-1 text-sm text-ink-500">
            La cartera de facturas al crédito del periodo, con lo abonado y lo que falta cobrar.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={exportarPdf}
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
          >
            PDF
          </Button>

          <Button
            variant="outline"
            onClick={exportar}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
          >
            Exportar
          </Button>
          <Button
            variant="outline"
            onClick={() => void consulta.refetch()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Desde</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.desde}
                  onChange={(evento) => cambiar('desde', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Hasta</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.hasta}
                  onChange={(evento) => cambiar('hasta', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm sm:col-span-2">
              <span className="font-medium text-ink-700">
                Cliente{' '}
                <span className="font-normal text-ink-400">
                  {catalogos.data
                    ? `(${formatNumber(clientes.length)} con facturas por cobrar)`
                    : ''}
                </span>
              </span>
              <Select
                value={filtros.numero_doc}
                onChange={({ target }) => cambiar('numero_doc', target.value)}
                className={CONTROL}
                aria-label="Cliente"
              >
                <option value="">TODOS</option>
                {clientes.map((cliente) => (
                  <option key={cliente.documento ?? cliente.cliente} value={cliente.documento ?? ''}>
                    {cliente.cliente} · {cliente.documento ?? 'sin documento'} (
                    {formatNumber(cliente.pendiente)})
                  </option>
                ))}
              </Select>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-4 border-t border-ink-100 pt-3">
            <label className="inline-flex items-center gap-2 text-xs font-semibold text-ink-700">
              <input
                type="checkbox"
                className="size-4 accent-brand-600"
                checked={filtros.solo_pendientes}
                onChange={(evento) => cambiar('solo_pendientes', evento.target.checked)}
              />
              Sólo con saldo (abonado menor que el pendiente)
            </label>

            <label className="inline-flex items-center gap-2 text-xs font-semibold text-ink-700">
              <input
                type="checkbox"
                className="size-4 accent-brand-600"
                checked={filtros.vencidas}
                onChange={(evento) => cambiar('vencidas', evento.target.checked)}
              />
              Sólo vencidas
            </label>

            {meses.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500">
                  <Search className="size-3.5" aria-hidden="true" />
                  Meses con facturas
                </span>
                {meses.slice(0, 12).map((mes) => {
                  const rango = rangoDe(mes.periodo);
                  const activo = rango.desde === filtros.desde && rango.hasta === filtros.hasta;

                  return (
                    <button
                      key={mes.periodo}
                      type="button"
                      aria-pressed={activo}
                      onClick={() => setFiltros((previos) => ({ ...previos, ...rango }))}
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                        activo
                          ? 'border-brand-300 bg-brand-50 text-brand-700'
                          : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                      )}
                      title={`${formatNumber(mes.facturas)} factura(s)`}
                    >
                      {etiquetaMes(mes.periodo)}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar el reporte">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo exportar">
          {aviso}
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        {[
          {
            etiqueta: 'Facturas',
            valor: formatNumber(resumen?.facturas ?? 0),
            detalle: `${formatNumber(resumen?.vencidas ?? 0)} vencida(s)`,
          },
          {
            etiqueta: 'Total facturado',
            valor: formatCurrency(resumen?.total ?? 0),
            detalle: 'con retención y abonos',
          },
          {
            etiqueta: 'Retención',
            valor: formatCurrency(resumen?.retencion ?? 0),
            detalle: 'del periodo',
          },
          {
            etiqueta: 'Abonado',
            valor: formatCurrency(resumen?.abonado ?? 0),
            detalle: 'pagos recibidos',
          },
          {
            etiqueta: 'Pendiente de pago',
            valor: formatCurrency(resumen?.pendiente ?? 0),
            detalle: `vencido ${formatCurrency(resumen?.pendiente_vencido ?? 0)}`,
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
              <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <h2 className="text-sm font-semibold text-ink-800">Monto pendiente por cliente</h2>

          {consulta.isLoading ? <Skeleton className="h-32 w-full" /> : null}

          {!consulta.isLoading ? (
            <div className="max-h-[40vh] overflow-y-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Cliente</th>
                    <th className="w-[7rem] px-2 py-2">Documento</th>
                    <th className="w-[5rem] px-2 py-2 text-right">Facturas</th>
                    <th className="w-[7rem] px-2 py-2 text-right">Total</th>
                    <th className="w-[7rem] px-2 py-2 text-right">Abonado</th>
                    <th className="w-[7rem] px-2 py-2 text-right">Pendiente</th>
                  </tr>
                </thead>
                <tbody>
                  {porCliente.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={6} className="px-2 py-8 text-center text-sm text-ink-500">
                        No hay facturas por cobrar que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {porCliente.map((fila) => (
                    <tr
                      key={fila.documento ?? fila.cliente}
                      className="border-t border-ink-100 hover:bg-brand-50/30"
                    >
                      <td className="px-2 py-2">
                        <p className="truncate font-semibold text-ink-900" title={fila.cliente ?? ''}>
                          {fila.cliente ?? '—'}
                        </p>
                        {fila.vencidas > 0 ? (
                          <Badge variant="danger" className="mt-0.5 px-1.5 py-0 text-[10px]">
                            {formatNumber(fila.vencidas)} vencida(s)
                          </Badge>
                        ) : null}
                      </td>
                      <td className="px-2 py-2 text-ink-700">{fila.documento ?? '—'}</td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatNumber(fila.facturas)}
                      </td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatCurrency(fila.total)}
                      </td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatCurrency(fila.abonado)}
                      </td>
                      <td className="px-2 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(fila.pendiente)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {porCliente.length > 0 ? (
                  <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                    <tr className="text-[13px] font-semibold text-ink-800">
                      <td className="px-2 py-2" colSpan={2}>
                        TOTALES · {formatNumber(porCliente.length)} cliente(s)
                      </td>
                      <td className="px-2 py-2 text-right">
                        {formatNumber(resumen?.facturas ?? 0)}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {formatCurrency(resumen?.total ?? 0)}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {formatCurrency(resumen?.abonado ?? 0)}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {formatCurrency(resumen?.pendiente ?? 0)}
                      </td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-800">
              Facturas del periodo ({formatNumber(resumen?.facturas ?? 0)})
            </h2>
            <span className="text-xs text-ink-500">
              {formatDateValue(filtros.desde)} — {formatDateValue(filtros.hasta)} · por fecha de
              emisión
            </span>
          </div>

          {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

          {!consulta.isLoading ? (
            <>
            <div className="max-h-[60vh] overflow-y-auto rounded-card border border-ink-100">
              <table className="w-full table-fixed border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="w-[5.5rem] px-1.5 py-2">Emisión</th>
                    <th className="w-[5.5rem] px-1.5 py-2">Vence</th>
                    <th className="px-1.5 py-2">Cliente</th>
                    <th className="w-[6.5rem] px-1.5 py-2">Factura</th>
                    <th className="w-[5.5rem] px-1.5 py-2 text-right">Total</th>
                    <th className="w-[5.5rem] px-1.5 py-2 text-right">Abonado</th>
                    <th className="w-[5.5rem] px-1.5 py-2 text-right">Pendiente</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={7} className="px-1.5 py-10 text-center text-sm text-ink-500">
                        No hay facturas por cobrar que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {paginacion.visibles.map((fila) => (
                    <tr
                      key={fila.comprobante_id}
                      className="border-t border-ink-100 hover:bg-brand-50/30"
                    >
                      <td className="px-1.5 py-2 text-ink-700">
                        {formatDateValue(fila.fecha_emision)}
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="text-ink-700">{formatDateValue(fila.fecha_vencimiento)}</p>
                        {fila.vencida ? (
                          <Badge variant="danger" className="mt-0.5 px-1.5 py-0 text-[10px]">
                            {formatNumber(fila.dias_vencimiento ?? 0)} días
                          </Badge>
                        ) : null}
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate font-semibold text-ink-900" title={fila.cliente ?? ''}>
                          {fila.cliente ?? '—'}
                        </p>
                        <p className="truncate text-[11px] text-ink-500">
                          {fila.documento ?? 'sin documento'} · {fila.condicion_pago ?? '—'}
                        </p>
                        {fila.observaciones ? (
                          <p
                            className="truncate text-[11px] text-ink-400"
                            title={fila.observaciones}
                          >
                            {fila.observaciones}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-1.5 py-2 font-semibold text-ink-800">{fila.factura}</td>
                      <td className="px-1.5 py-2 text-right text-ink-700">
                        {formatCurrency(fila.total)}
                      </td>
                      <td className="px-1.5 py-2 text-right text-ink-700">
                        {formatCurrency(fila.abonado)}
                      </td>
                      <td className="px-1.5 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(fila.pendiente)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {filas.length > 0 ? (
                  <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                    <tr className="text-[13px] font-semibold text-ink-800">
                      <td className="px-1.5 py-2" colSpan={4}>
                        SUMA · {formatNumber(resumen?.facturas ?? 0)} factura(s)
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        {formatCurrency(resumen?.total ?? 0)}
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        {formatCurrency(resumen?.abonado ?? 0)}
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        {formatCurrency(resumen?.pendiente ?? 0)}
                      </td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>

            <Paginador {...paginacion} etiqueta="facturas" />
            </>
          ) : null}
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          Sólo se listan los comprobantes <strong>al crédito</strong> (no de contado) que no están
          anulados ni son de pago adelantado, y el rango va por la fecha de emisión. «Retención» es
          lo detraído, «abonado» lo ya cobrado y «pendiente» el saldo del comprobante
          (<code>pending_amount</code>); el panel anterior mostraba exactamente estos cuatro importes
          y su total, pero sin distinguir las vencidas.
        </span>
      </p>
    </div>
  );
}
